import { IThroughputHistory } from '@abrController/DataCollectors/ThroughputHistory/IThroughputHistory';
import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { TEventBus } from '@eventBus/EventBus';
import { NetworkLoaderEvent } from '@streamLoader/net/NetworkLoader.events';
import { INetworkLoadStats } from '@streamLoader/net/helpers/NetworkLoadStats';
import bind from '@utils/decorators/bind';

import { IThroughputHistoryConfig } from './ThroughputHistory.config';
import { EWMA } from '../../helpers/EWMA';

export class ThroughputHistory extends ConfigConsumer<IThroughputHistoryConfig> {
  private _measuredSpeedEwmaInstance: EWMA;
  private _realSpeedEwmaInstance: EWMA;
  private _ttfbEwmaInstance: EWMA;
  private readonly _onNetMetricsReadyListener: string;

  constructor(eventBus: TEventBus) {
    super('throughputHistory', eventBus);

    this._measuredSpeedEwmaInstance = new EWMA(this.config.ewmaFastAlpha, this.config.ewmaSlowAlpha, this._eventBus);
    this._realSpeedEwmaInstance = new EWMA(this.config.ewmaFastAlpha, this.config.ewmaSlowAlpha, this._eventBus);
    this._ttfbEwmaInstance = new EWMA(this.config.ewmaFastAlpha, this.config.ewmaSlowAlpha, this._eventBus);
    this._onNetMetricsReadyListener = this._eventBus.subscribe(NetworkLoaderEvent.stats.statsReady, this._onNetworkMetricsReady);
  }

  destroy(): void {
    super.destroy();

    this._eventBus.unsubscribe(this._onNetMetricsReadyListener);

    this._measuredSpeedEwmaInstance.reset();
    this._realSpeedEwmaInstance.reset();
  }

  public getCurrentBitrateEstimate(): IThroughputHistory {
    return {
      measuredSpeed: this._measuredSpeedEwmaInstance.getCurrentValue(),
      realSpeedWithoutTTFB: this._realSpeedEwmaInstance.getCurrentValue(),
      ttfb: this._ttfbEwmaInstance.getCurrentMaximalValue(),
    };
  }

  public reset(): void {
    this._measuredSpeedEwmaInstance = new EWMA(this.config.ewmaFastAlpha, this.config.ewmaSlowAlpha, this._eventBus);
    this._realSpeedEwmaInstance = new EWMA(this.config.ewmaFastAlpha, this.config.ewmaSlowAlpha, this._eventBus);
    this._ttfbEwmaInstance = new EWMA(this.config.ewmaFastAlpha, this.config.ewmaSlowAlpha, this._eventBus);
  }

  @bind
  public _handleConfigUpdated(updatedValues: Partial<IThroughputHistoryConfig>): void {
    if (updatedValues.ewmaFastAlpha || updatedValues.ewmaSlowAlpha) {
      this.reset();
    }
  }

  @bind
  private _onNetworkMetricsReady(stats: INetworkLoadStats): void {
    if (this.config.ignorePlaylistsMetrics && stats.type === 'text') {
      return;
    }

    const realSpeed: number = (stats.sizeInKb * 8) / ((stats.downloadDuration - stats.ttfb) / 1000);

    this._measuredSpeedEwmaInstance.addValue(stats.calculatedSpeed);
    this._realSpeedEwmaInstance.addValue(realSpeed);
    this._ttfbEwmaInstance.addValue(stats.ttfb / 1000);
  }
}
